import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { AlertCard } from "~/components/ui/card/AlertCard";
import { FEATURE_FLAGS } from "~/featureFlags/consts";
import { useFeatureFlag } from "~/featureFlags/useFeatureFlag";
import { fetchSiteIntegrationsWithAuth } from "~/hooks/useGoogleDashboard";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";
import { routes } from "~/router";

import styles from "./ClientReports.module.scss";
import { findIntegrationGaps } from "./googleIntegrations";

const REASON_LABELS = {
  not_connected: "isn't connected",
  no_property: "has no property selected",
};

const GoogleIntegrationsNotice = ({ siteId }: { siteId: number }) => {
  // The Integrations tab (Google OAuth) is switched off on white-label
  // hosts, so there is nowhere to send the user to connect an account.
  const canConnectGoogle = useFeatureFlag(
    FEATURE_FLAGS.SITES_SETTINGS_GOOGLE_INTEGRATIONS,
  );
  const { data } = useQuery({
    queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
    queryFn: () => fetchSiteIntegrationsWithAuth(siteId),
  });

  if (!data) return null;

  const gaps = findIntegrationGaps(data.integrations);

  if (gaps.length === 0) return null;

  return (
    <AlertCard
      type="warning"
      title="Some report sections will be left out"
      content={
        <ul className={styles.integrationGaps}>
          {gaps.map((gap) => (
            <li key={gap.kind}>
              <strong>{gap.name}</strong> {REASON_LABELS[gap.reason]}, so
              reports won&apos;t include {gap.data}.
            </li>
          ))}
        </ul>
      }
      actions={
        canConnectGoogle && (
          <Link
            to={routes.site.settings.tab({
              id: String(siteId),
              tabId: "integrations",
            })}
          >
            Set up integrations
          </Link>
        )
      }
    />
  );
};

export default GoogleIntegrationsNotice;
